import { validateThreadNgWords } from "@/features/thread/utils/threadNgWords";

export const THREAD_NG_PRESETS_KEY = "nijiurachan-thread-ng-presets-v1";
export const THREAD_NG_PRESETS_UPDATED_EVENT =
  "nijiurachan:thread-ng-presets-updated";

export interface ThreadNgPreset {
  name: string;
  patterns: string[];
}

type PresetStorage = Pick<Storage, "getItem" | "setItem">;

function browserStorage(): Storage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

function notifyPresetsUpdated(storage: object): void {
  if (storage === browserStorage() && typeof window !== "undefined") {
    window.dispatchEvent(new Event(THREAD_NG_PRESETS_UPDATED_EVENT));
  }
}

export function readThreadNgPresets(
  storage: PresetStorage | null = browserStorage(),
): ThreadNgPreset[] {
  try {
    const data: unknown = JSON.parse(
      storage?.getItem(THREAD_NG_PRESETS_KEY) ?? "null",
    );
    if (!Array.isArray(data)) return [];
    return data.filter((item): item is ThreadNgPreset => {
      if (!item || typeof item !== "object") return false;
      const preset = item as Partial<ThreadNgPreset>;
      return (
        typeof preset.name === "string" &&
        preset.name.trim() !== "" &&
        Array.isArray(preset.patterns) &&
        preset.patterns.every((pattern) => typeof pattern === "string") &&
        validateThreadNgWords(preset.patterns.join("\n")).error === null
      );
    });
  } catch {
    return [];
  }
}

/** Returns false when storage rejects the write or cannot read it back. */
export function writeThreadNgPresets(
  presets: ThreadNgPreset[],
  storage: PresetStorage | null = browserStorage(),
): boolean {
  if (!storage) return false;
  try {
    const serialized = JSON.stringify(presets);
    storage.setItem(THREAD_NG_PRESETS_KEY, serialized);
    const saved = storage.getItem(THREAD_NG_PRESETS_KEY) === serialized;
    if (saved) notifyPresetsUpdated(storage);
    return saved;
  } catch {
    return false;
  }
}

export function removeThreadNgPresets(
  storage: Pick<Storage, "getItem" | "removeItem"> | null = browserStorage(),
): boolean {
  if (!storage) return false;
  try {
    storage.removeItem(THREAD_NG_PRESETS_KEY);
    const removed = storage.getItem(THREAD_NG_PRESETS_KEY) === null;
    if (removed) notifyPresetsUpdated(storage);
    return removed;
  } catch {
    return false;
  }
}
